import { __decorate } from "tslib";
import { AutoEncoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { v4 as uuidv4 } from "uuid";
import { GroupPrivateSettings } from './GroupPrivateSettings';
import { GroupSettings, WaitingListType } from './GroupSettings';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { PermissionLevel } from './Permissions';
export var GroupStatus;
(function (GroupStatus) {
    GroupStatus["Open"] = "Open";
    GroupStatus["Closed"] = "Closed";
    GroupStatus["Archived"] = "Archived";
})(GroupStatus || (GroupStatus = {}));
export class Group extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.cycle = 0;
        this.createdAt = new Date();
        this.deletedAt = null;
        /**
         * Only set when you have access to this information
         */
        this.privateSettings = null;
        /**
     * Manually close a group
     */
        this.status = GroupStatus.Open;
    }
    getTimeRange(cycle = 0) {
        if (cycle === this.cycle) {
            return this.settings.dateRangeDescription;
        }
        const cycleInfo = this.settings.cycleSettings.get(cycle);
        if (!cycleInfo || !cycleInfo.dateRangeDescription) {
            return this.settings.getEstimatedTimeRange(this.cycle - cycle);
        }
        return cycleInfo.dateRangeDescription;
    }
    getTimeRangeOffset(cycleOffset = 0) {
        return this.getTimeRange(this.cycle - cycleOffset);
    }
    static defaultSort(a, b) {
        if (a.settings.maxAge && !b.settings.maxAge) {
            return -1;
        }
        if (b.settings.maxAge && !a.settings.maxAge) {
            return 1;
        }
        if (!b.settings.maxAge && !a.settings.maxAge) {
            // name
            return Group.nameSort(a, b);
        }
        if (a.settings.maxAge > b.settings.maxAge) {
            return 1;
        }
        if (a.settings.maxAge < b.settings.maxAge) {
            return -1;
        }
        return Group.nameSort(a, b);
    }
    static nameSort(a, b) {
        if (a.settings.name.toLowerCase() < b.settings.name.toLowerCase()) {
            return -1;
        }
        if (a.settings.name.toLowerCase() > b.settings.name.toLowerCase()) {
            return 1;
        }
        return 0;
    }
    getMemberCount({ cycle, cycleOffset, waitingList } = {}) {
        if (cycleOffset) {
            cycle = this.cycle - cycleOffset;
        }
        return this.settings.getMemberCount({ cycle, waitingList });
    }
    /**
     * Return the pre registration date only if is is active right now
     */
    get activePreRegistrationDate() {
        if (!this.settings.registrationStartDate) {
            // Registration start date is a requirement for pre registrations
            return null;
        }
        if (this.settings.registrationStartDate < new Date() || this.settings.waitingListType !== WaitingListType.PreRegistrations) {
            // Start date is in the past: registrations are open
            return null;
        }
        return this.settings.preRegistrationsDate;
    }
    /**
     * Closed now, but will open in the future
     */
    get notYetOpen() {
        if (!this.settings.registrationStartDate) {
            return false;
        }
        const now = new Date();
        const preRegistrationDate = this.activePreRegistrationDate;
        if (this.settings.registrationStartDate > now && (!preRegistrationDate || preRegistrationDate > now)) {
            // Start date or pre registration date are in the future
            return true;
        }
        return false;
    }
    /**
     * No registrations and waiting list registrations are possible if closed
     */
    get closed() {
        if (this.status !== GroupStatus.Open) {
            return true;
        }
        if (this.notYetOpen) {
            // Start date or pre registration date are in the future
            return true;
        }
        const now = new Date();
        if (this.settings.registrationEndDate && this.settings.registrationEndDate < now) {
            return true;
        }
        return false;
    }
    hasWaitingList() {
        return this.settings.canHaveWaitingList;
    }
    /**
     * Returns all parent and grandparents of this group
     */
    getParentCategories(all, recursive = true) {
        const map = new Map();
        const parents = all.filter(g => g.groupIds.includes(this.id));
        for (const parent of parents) {
            map.set(parent.id, parent);
            if (recursive) {
                const hisParents = parent.getParentCategories(all);
                for (const pp of hisParents) {
                    map.set(pp.id, pp);
                }
            }
        }
        return [...map.values()];
    }
    hasAccess(permissions, organization, permissionLevel = PermissionLevel.Read) {
        var _a, _b, _c, _d, _e;
        if (!permissions) {
            return false;
        }
        if ((_a = this.privateSettings) === null || _a === void 0 ? void 0 : _a.permissions.hasAccess(permissions, (_c = (_b = organization.privateMeta) === null || _b === void 0 ? void 0 : _b.roles) !== null && _c !== void 0 ? _c : [], permissionLevel)) {
            return true;
        }
        // Check parent categories
        const parentCategories = this.getParentCategories(organization.meta.categories);
        for (const category of parentCategories) {
            if (category.settings.permissions.groupPermissions.hasAccess(permissions, (_e = (_d = organization.privateMeta) === null || _d === void 0 ? void 0 : _d.roles) !== null && _e !== void 0 ? _e : [], permissionLevel)) {
                return true;
            }
        }
        return false;
    }
    isPublic(allCategories) {
        for (const parent of this.getParentCategories(allCategories)) {
            if (!parent.settings.public) {
                return false;
            }
        }
        return true;
    }
    /**
     * Whetever a given user has access to the members in this group.
     */
    hasReadAccess(permissions, organization) {
        return this.hasAccess(permissions, organization, PermissionLevel.Read);
    }
    /**
     * Whetever a given user has access to the members in this group.
     */
    hasWriteAccess(permissions, organization) {
        return this.hasAccess(permissions, organization, PermissionLevel.Write);
    }
    /**
     * Whetever a given user has access to the members in this group.
     */
    hasFullAccess(permissions, organization) {
        return this.hasAccess(permissions, organization, PermissionLevel.Full);
    }
    get squareImage() {
        var _a;
        return (_a = this.settings.squarePhoto) !== null && _a !== void 0 ? _a : this.settings.coverPhoto;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Group.prototype, "id", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], Group.prototype, "cycle", void 0);
__decorate([
    field({ decoder: GroupSettings })
], Group.prototype, "settings", void 0);
__decorate([
    field({ decoder: DateDecoder, version: 187 })
], Group.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 187 })
], Group.prototype, "deletedAt", void 0);
__decorate([
    field({ decoder: GroupPrivateSettings, nullable: true, version: 10 })
], Group.prototype, "privateSettings", void 0);
__decorate([
    field({ decoder: new EnumDecoder(GroupStatus), version: 192 })
], Group.prototype, "status", void 0);
export const GroupPatch = Group.patchType();
//# sourceMappingURL=Group.js.map